import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import BatteryFullIcon from "@mui/icons-material/BatteryFull";
import BatteryAlertIcon from "@mui/icons-material/BatteryAlert";
import BatteryUnknownIcon from "@mui/icons-material/BatteryUnknown";

/**
 * Premium pill for Battery Life (%) column. Color by level: green (high), amber (medium), red (low). N/A for NFC.
 */
export default function BatteryLifePill({ percent, isNfc }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  if (isNfc || percent == null) {
    return (
      <Box sx={wrapperSx}>
        <Box
          component="span"
          sx={{
            ...pillBaseSx,
            backgroundColor: isDark ? "hsl(215, 15%, 32%)" : "rgba(100, 116, 139, 0.2)",
            color: isDark ? "rgba(255,255,255,0.85)" : theme.palette.text.secondary,
            border: isDark ? "1px solid rgba(148, 163, 184, 0.15)" : "1px solid rgba(100, 116, 139, 0.25)",
            "&:focus-visible": {
              outline: "none",
              boxShadow: "0 0 0 2px rgba(160, 196, 109, 0.4)",
            },
            "@media (prefers-reduced-motion: reduce)": {
              transition: "none",
            },
          }}
        >
          <BatteryUnknownIcon />
          <Box component="span" sx={{ minWidth: "2.5em", textAlign: "right" }}>
            N/A
          </Box>
        </Box>
      </Box>
    );
  }

  const value = typeof percent === "number" ? percent : parseFloat(percent);
  const isHigh = value >= 50;
  const isLow = value < 20;
  const Icon = isLow ? BatteryAlertIcon : BatteryFullIcon;

  const pillStyles = isHigh
    ? {
        backgroundColor: isDark ? theme.palette.success.dark : theme.palette.success.main,
        color: theme.palette.success.contrastText ?? "#fff",
        border: isDark ? "1px solid rgba(160, 196, 109, 0.25)" : "1px solid rgba(122, 156, 74, 0.35)",
      }
    : isLow
      ? {
          backgroundColor: isDark ? theme.palette.error.dark : theme.palette.error.main,
          color: theme.palette.error.contrastText ?? "#fff",
          border: isDark ? "1px solid rgba(239, 68, 68, 0.3)" : "1px solid rgba(220, 38, 38, 0.35)",
        }
      : {
          backgroundColor: isDark ? theme.palette.warning.dark : theme.palette.warning.main,
          color: theme.palette.warning.contrastText ?? "#fff",
          border: isDark ? "1px solid rgba(245, 158, 11, 0.3)" : "1px solid rgba(217, 119, 6, 0.35)",
        };

  return (
    <Box sx={wrapperSx}>
      <Box component="span" sx={{ ...pillBaseSx, ...pillStyles }}>
        <Icon />
        <Box
          component="span"
          sx={{
            minWidth: "2.5em",
            textAlign: "right",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {value}%
        </Box>
      </Box>
    </Box>
  );
}

const wrapperSx = {
  display: "flex",
  width: "100%",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "100%",
};

// Match ManufacturerPill: same padding, font, icon, and minWidth so pill size is consistent for all content
const PILL_MIN_WIDTH = 100;
const pillBaseSx = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "6px",
  minWidth: PILL_MIN_WIDTH,
  paddingLeft: "10px",
  paddingRight: "10px",
  paddingTop: "3px",
  paddingBottom: "3px",
  borderRadius: "9999px",
  fontSize: "0.75rem",
  fontWeight: 600,
  letterSpacing: "0.02em",
  lineHeight: 1.3,
  whiteSpace: "nowrap",
  transition: "background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease",
  "&:focus-visible": {
    outline: "none",
    boxShadow: "0 0 0 2px rgba(160, 196, 109, 0.4)",
  },
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
  },
  "& .MuiSvgIcon-root": {
    fontSize: "0.875rem",
    opacity: 0.95,
    margin: 0,
    flexShrink: 0,
  },
};
